import { type TypedDocumentNode, gql } from '@apollo/client';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Locker } from '@sorare/core/src/atoms/icons/Locker';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab } from '@sorare/core/src/atoms/navigation/TabBar';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';

import type { LeagueTrckTab_so5LeagueTrack } from './__generated__/index.graphql';

const TooltipWrapper = styled(Horizontal).attrs({ center: true })`
  padding: var(--unit) var(--double-unit);
`;
const CardIconWrapper = styled(Horizontal).attrs({ center: true })`
  position: relative;
`;

const LockIconWrapper = styled.div`
  position: absolute;
  right: calc(-0.5 * var(--unit));
  display: inline-flex;
  top: calc(-1 * var(--unit));
  & > * {
    color: var(--c-nd-600);
    width: var(--unit);
  }
`;
type Props = {
  to: string;
  track: LeagueTrckTab_so5LeagueTrack;
};

const WithLockedIcon = ({ children }: PropsWithChildren) => {
  return (
    <CardIconWrapper>
      {children}
      <LockIconWrapper>
        <Locker />
      </LockIconWrapper>
    </CardIconWrapper>
  );
};

export const LeagueTrckTab = ({ track, to }: Props) => {
  const scarcityIcon = (
    <ScarcityIcon rarity={track.entrySo5Leaderboard.mainRarityType || 'mix'} />
  );
  return (
    <Tooltip
      title={
        !track.canCompose.value ? (
          <TooltipWrapper center gap={2}>
            <WithLockedIcon>{scarcityIcon}</WithLockedIcon>
            <Vertical gap={0.5}>
              <LabelM bold>
                <FormattedMessage
                  id="FixtureEnterSeasonalityCompetitionLayout.lockedTrackTitle"
                  defaultMessage="Locked mode"
                />
              </LabelM>
              <LabelS>
                <FormattedMessage
                  id="FixtureEnterSeasonalityCompetitionLayout.lockedTrackDescription"
                  defaultMessage="Cannot enter when Game Week is Live"
                />
              </LabelS>
            </Vertical>
          </TooltipWrapper>
        ) : (
          ''
        )
      }
    >
      <Tab
        key={track.slug}
        value={track.slug}
        disabled={!!track.completed || !track.canCompose.value}
        to={to}
        replace
      >
        <Horizontal center>
          {track.completed ? (
            <FontAwesomeIcon
              icon={faCircleCheck}
              color="var(--c-onBase-success)"
            />
          ) : (
            <>
              {track.canCompose.value ? (
                scarcityIcon
              ) : (
                <WithLockedIcon>{scarcityIcon}</WithLockedIcon>
              )}
            </>
          )}
          {track.displayName}
        </Horizontal>
      </Tab>
    </Tooltip>
  );
};

LeagueTrckTab.fragments = {
  so5LeagueTrack: gql`
    fragment LeagueTrckTab_so5LeagueTrack on So5LeagueTrack {
      slug
      displayName
      completed
      canCompose {
        value
      }
      entrySo5Leaderboard {
        slug
        mainRarityType
      }
    }
  ` as TypedDocumentNode<LeagueTrckTab_so5LeagueTrack>,
};
